/**
 * Handles wheel and swipe navigation between video phases and scroll mode.
 */
class NavigationController {
    /**
     * @param {Object} options
     * @param {Object} options.videoExperience
     * @param {Function} options.enterScrollMode
     * @param {Function} options.exitScrollModeToVideoPhaseTwo
     * @param {string} [options.initialPhase]
     */
    constructor({
        videoExperience,
        enterScrollMode,
        exitScrollModeToVideoPhaseTwo,
        initialPhase = 'video-phase-1',
        allowBackwardNavigation = true
    }) {
        this.videoExperience               = videoExperience;
        this.enterScrollMode               = enterScrollMode;
        this.exitScrollModeToVideoPhaseTwo = exitScrollModeToVideoPhaseTwo;
        this.currentPhase                  = initialPhase;
        this.allowBackwardNavigation       = allowBackwardNavigation;

        this.wheelDelta                     = 0;
        this.wheelThreshold                 = 200;
        this.wheelResetDelay                = 400;
        this.wheelResetTimeout              = null;
        this.scrollModeWheelMultiplier      = 3;
        this.scrollModeExitBackwardCooldown = 700;
        this.lastScrollModeExitTime         = 0;
        this.isNavigationLocked             = false;
        this.navigationUnlockTimeout        = null;

        this.touchStartY            = 0;
        this.touchStartTime         = 0;
        this.touchDistanceThreshold = 40;
        this.touchVelocityThreshold = 0.3;

        this.handleWheel      = this.handleWheel.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchEnd   = this.handleTouchEnd.bind(this);

        window.addEventListener('wheel', this.handleWheel, { passive : false });
        window.addEventListener('touchstart', this.handleTouchStart, { passive : true });
        window.addEventListener('touchend', this.handleTouchEnd, { passive : true });
    }

    /**
     * Update current navigation phase.
     * @param {string} phase
     */
    setPhase(phase) {
        this.currentPhase = phase;
    }

    /**
     * Handle wheel navigation between phases.
     * @param {WheelEvent} event
     */
    handleWheel(event) {
        if (this.currentPhase === 'preloader' || this.currentPhase === 'scroll-mode') {
            return;
        }

        if (this.isNavigationLocked) {
            event.preventDefault();
            return;
        }

        const { deltaY } = event;

        if (Math.abs(deltaY) < 5) {
            return;
        }

        event.preventDefault();

        this.wheelDelta += deltaY;

        if (this.wheelResetTimeout) {
            window.clearTimeout(this.wheelResetTimeout);
        }

        this.wheelResetTimeout = window.setTimeout(() => {
            this.wheelDelta = 0;
        }, this.wheelResetDelay);

        if (this.wheelDelta > this.wheelThreshold) {
            // eslint-disable-next-line no-console
            console.log('[NavigationController] wheel navigate forward', {
                deltaY,
                wheelDelta : this.wheelDelta,
                phase      : this.currentPhase
            });
            this.wheelDelta = 0;
            this.navigate(1);
        } else if (this.wheelDelta < -this.wheelThreshold) {
            // eslint-disable-next-line no-console
            console.log('[NavigationController] wheel navigate backward', {
                deltaY,
                wheelDelta : this.wheelDelta,
                phase      : this.currentPhase
            });
            this.wheelDelta = 0;
            this.navigate(-1);
        }
    }

    /**
     * Handle touch start for swipe navigation.
     * @param {TouchEvent} event
     */
    handleTouchStart(event) {
        if (this.currentPhase === 'scroll-mode' || this.currentPhase === 'preloader' || this.isNavigationLocked) {
            return;
        }

        if (!event.changedTouches || event.changedTouches.length === 0) {
            return;
        }

        const touch = event.changedTouches[0];

        this.touchStartY    = touch.clientY;
        this.touchStartTime = Date.now();
    }

    /**
     * Handle touch end for swipe navigation.
     * @param {TouchEvent} event
     */
    handleTouchEnd(event) {
        if (this.currentPhase === 'scroll-mode' || this.currentPhase === 'preloader' || this.isNavigationLocked) {
            return;
        }

        if (!event.changedTouches || event.changedTouches.length === 0) {
            return;
        }

        const touch   = event.changedTouches[0];
        const deltaY  = touch.clientY - this.touchStartY;
        const elapsed = Date.now() - this.touchStartTime;

        if (elapsed <= 0) {
            return;
        }

        const distance = Math.abs(deltaY);
        const velocity = distance / elapsed;

        const scrollTop = window.pageYOffset
            || document.documentElement.scrollTop
            || document.body.scrollTop
            || 0;

        // eslint-disable-next-line no-console
        console.log('[NavigationController] touchEnd', {
            phase : this.currentPhase,
            deltaY,
            elapsed,
            distance,
            velocity,
            scrollTop
        });

        if (this.currentPhase === 'scroll-mode') {
            // В scroll-mode уходим назад только свайпом вниз из верхней точки
            if (scrollTop > 10 || deltaY <= 0) {
                return;
            }

            const distanceThreshold = this.touchDistanceThreshold * 1.5;
            const velocityThreshold = this.touchVelocityThreshold * 1.2;

            if (distance < distanceThreshold || velocity < velocityThreshold) {
                return;
            }

            this.navigate(-1);
            return;
        }

        if (distance < this.touchDistanceThreshold || velocity < this.touchVelocityThreshold) {
            return;
        }

        if (deltaY < 0) {
            this.navigate(1);
        } else {
            this.navigate(-1);
        }
    }

    /**
     * Navigate between phases.
     * @param {number} direction 1 for forward, -1 for backward.
     */
    navigate(direction) {
        if (direction < 0 && !this.allowBackwardNavigation) {
            return;
        }

        if (direction > 0) {
            this.navigateForward();
        } else if (direction < 0) {
            this.navigateBackward();
        }
    }

    /**
     * Navigate forward between video phases and scroll mode.
     */
    navigateForward() {
        if (!this.videoExperience) {
            return;
        }

        if (this.currentPhase === 'video-phase-1') {
            // eslint-disable-next-line no-console
            console.log('[NavigationController] navigateForward from video-phase-1', {
                isStageOneInteractive : this.videoExperience.isStageOneInteractive
            });

            if (!this.videoExperience.isStageOneInteractive) {
                return;
            }

            this.lockNavigation(1500);
            this.videoExperience.videoStageTwo();
            this.currentPhase = 'video-phase-2';
            return;
        }

        if (this.currentPhase === 'video-phase-2') {
            // eslint-disable-next-line no-console
            console.log('[NavigationController] navigateForward from video-phase-2', {
                isStageTwoInteractive : this.videoExperience.isStageTwoInteractive
            });

            if (!this.videoExperience.isStageTwoInteractive) {
                return;
            }

            if (typeof this.enterScrollMode === 'function') {
                this.lockNavigation(4500);
                this.currentPhase = 'scroll-mode';
                this.enterScrollMode();
            }
        }
    }

    /**
     * Navigate backward between video phases.
     * Currently reserved for future use.
     */
    navigateBackward() {
        if (!this.videoExperience) {
            return;
        }

        if (this.currentPhase === 'scroll-mode') {
            if (typeof this.exitScrollModeToVideoPhaseTwo === 'function') {
                this.lockNavigation(4000);
                this.currentPhase = 'video-phase-2';
                this.exitScrollModeToVideoPhaseTwo();
                this.lastScrollModeExitTime = Date.now();
            }
            return;
        }

        if (this.currentPhase === 'video-phase-2') {
            const now = Date.now();

            if (now - this.lastScrollModeExitTime < this.scrollModeExitBackwardCooldown) {
                return;
            }

            this.lockNavigation(1500);
            this.videoExperience.reverseToStageOne();
            this.currentPhase = 'video-phase-1';
        }
    }

    lockNavigation(duration = 1200) {
        this.isNavigationLocked = true;

        if (this.navigationUnlockTimeout) {
            window.clearTimeout(this.navigationUnlockTimeout);
        }

        this.navigationUnlockTimeout = window.setTimeout(() => {
            this.isNavigationLocked = false;
            this.wheelDelta         = 0;
        }, duration);
    }

    /**
     * Remove all listeners and timers.
     */
    destroy() {
        // Remove event listeners
        window.removeEventListener('wheel', this.handleWheel);
        window.removeEventListener('touchstart', this.handleTouchStart);
        window.removeEventListener('touchend', this.handleTouchEnd);

        // Clear all timeouts
        if (this.wheelResetTimeout) {
            window.clearTimeout(this.wheelResetTimeout);
            this.wheelResetTimeout = null;
        }

        if (this.navigationUnlockTimeout) {
            window.clearTimeout(this.navigationUnlockTimeout);
            this.navigationUnlockTimeout = null;
        }

        // Reset state
        this.wheelDelta = 0;
        this.isNavigationLocked = false;
        this.currentPhase = null;
        this.videoExperience = null;
        this.enterScrollMode = null;
        this.exitScrollModeToVideoPhaseTwo = null;
    }
}

export default NavigationController;
